// src/api/userService.ts
import { apiClient } from './apiClient';
import type { UserProfile, UserPlan } from '@/types';
import { getMockProfileFromStorage, updateMockProfileInStorage } from '../utils/mockUser';

const IS_MOCK_MODE = process.env.NODE_ENV === 'development' && localStorage.getItem('mockUser') === 'true';

export const userService = {
  /**
   * Pobiera pełny profil użytkownika.
   */
  getUserProfile: async (token: string | null): Promise<UserProfile> => {
    if (IS_MOCK_MODE) {
      const local = getMockProfileFromStorage();
      // Backend is SoT for plan + AI quota (same path as real users via x-test-user-id).
      // Never keep a stale local Free over Cosmos Pro — that desyncs AccessBanner vs AiLimitPaywall.
      if (token) {
        try {
          const remote = await apiClient.get('/api/me', { token }) as UserProfile;
          const merged: UserProfile = {
            ...local,
            ...remote,
            plan: remote.plan || local.plan,
            queriesLeft: typeof remote.queriesLeft === 'number'
              ? remote.queriesLeft
              : local.queriesLeft,
          };
          updateMockProfileInStorage(merged);
          return merged;
        } catch {
          return local;
        }
      }
      return local;
    }
    return await apiClient.get('/api/me', { token });
  },

  /**
   * NOWA METODA: Wymusza synchronizację z HubSpotem
   * Wywoływana tylko przy logowaniu.
   */
  syncUser: async (token: string | null): Promise<void> => {
    if (IS_MOCK_MODE) {
        console.log("Mock Sync User with HubSpot skipped.");
        return;
    }
    if (!token) return;

    // "Fire and forget" - nie musimy czekać na wynik w UI, ale awaitujemy wysłanie
    try {
        await apiClient.post('/api/sync-user', {}, { token });
    } catch (error) {
        console.warn("Background user sync failed:", error);
    }
  },

  /**
   * DEV mock only — local plan flip for UI. Production has no /api/mock/upgrade.
   * Real plan changes go through Stripe checkout / webhooks.
   */
  updatePlan: async (newPlan: UserPlan, _token: string | null): Promise<UserProfile> => {
      if (IS_MOCK_MODE) {
          return updateMockProfileInStorage({ plan: newPlan });
      }
      throw new Error(
        'updatePlan is DEV mockUser-only. Use Stripe billing for real plan changes; the mock upgrade API route was removed.'
      );
  }
};